import { Button, ButtonTypes, MoveableDialog, TextArea } from "@repo/ui";
import { useMemo, useState } from "react";

import type { ApplicationSetting, ApplicationSettingDefinition } from "../../../models";
import type { DefinitionTree, Office, Segment } from "../settingsTree";

import { AppSettingSaveStatus, ApplicationSettingType } from "../../../models";
import { useSaveSettingMutation } from "../hooks";
import {
	enumValue,
	fillPlaceholders,
	normalizeValue,
	pickedNameParts,
	placeholderOptions,
	segmentOfRow,
	segmentToSave,
} from "../settingForm";
import { isPlaceholder, matchDefinition } from "../settingsTree";
import { DefinitionCascade } from "./definitionCascade";
import { OverwriteDialog } from "./overwriteDialog";
import { PlaceholderInput } from "./placeholderInput";
import { SegmentSelectors } from "./segmentSelectors";
import style from "./style.module.scss";
import { ValueInput } from "./valueInput";

interface Props {
	tree: DefinitionTree;
	offices: Office[];
	/** The row to edit. Its name stays as it is; its value, segment and notes can change */
	setting?: ApplicationSetting;
	/** The setting to add a row for (Add override). Without this or a row to edit, it's picked through the definition cascade */
	name?: string;
	/** The segment to start a new row with */
	segment: Segment;
	onClose: () => void;
}

const SAVE_FAILED = "The setting couldn't be saved.";

/** The dropdown grids have no width of their own, so this sets how many fit on a row */
const MIN_SIZE = { width: 900, height: 320 };

/** The definition's default, in the form its input shows */
const defaultValueOf = (definition?: ApplicationSettingDefinition) =>
	normalizeValue(definition?.defaultValue, definition?.dataType, definition?.enumOptions ?? []);

/** A row's value, in the form its input shows */
const valueOf = (setting: ApplicationSetting, definition?: ApplicationSettingDefinition) =>
	normalizeValue(setting.value, definition?.dataType ?? setting.dataType, definition?.enumOptions ?? []);

/**
 * Adds a setting row, or edits one. When adding over a row that already exists for the name and segment,
 * it asks before overwriting that row
 */
export function SettingDialog({ tree, offices, setting: editing, name, segment: startSegment, onClose }: Props) {
	const fixedName = editing?.name ?? name;
	const fixedDefinition = useMemo(
		() => (fixedName ? matchDefinition(tree, fixedName)?.node.definition : undefined),
		[tree, fixedName],
	);
	const [path, setPath] = useState<string[]>([]);
	const [leaf, setLeaf] = useState<string>();
	const [fills, setFills] = useState<string[]>([]);
	const [value, setValue] = useState(() => {
		// A secret comes without its value, so its box starts empty; saving it blank keeps the stored value
		if (editing?.isMasked) return "";
		return editing ? valueOf(editing, fixedDefinition) : defaultValueOf(fixedDefinition);
	});
	const [segment, setSegment] = useState(() => (editing ? segmentOfRow(editing, offices) : startSegment));
	const [notes, setNotes] = useState(editing?.notes ?? "");
	const [error, setError] = useState<string>();
	const [existing, setExisting] = useState<ApplicationSetting>();
	const save = useSaveSettingMutation();

	const nameParts = useMemo(
		() => (fixedName ? undefined : pickedNameParts(tree, path, leaf)),
		[tree, fixedName, path, leaf],
	);
	const placeholders = nameParts?.filter(isPlaceholder) ?? [];
	const placeholderChoices = useMemo(() => (nameParts ? placeholderOptions(tree, nameParts) : []), [tree, nameParts]);
	const settingName = fixedName ?? (nameParts && fillPlaceholders(nameParts, fills));
	const definition = fixedName ? fixedDefinition : leaf ? tree.nodes.get(leaf.toLowerCase())?.definition : undefined;
	// An orphan being edited has no definition, so its own type is used
	const dataType = definition?.dataType ?? editing?.dataType;
	const enumOptions = (dataType === ApplicationSettingType.Enum && definition?.enumOptions) || [];
	// Shown until an option replaces a saved value the dropdown can't show
	const savedNotAnOption =
		enumValue(editing?.value, enumOptions)?.matched === false && enumValue(value, enumOptions)?.matched === false;

	function pickDefinition(newPath: string[], newLeaf?: string) {
		setPath(newPath);
		setLeaf(newLeaf);
		setFills([]);
		setValue(defaultValueOf(newLeaf ? tree.nodes.get(newLeaf.toLowerCase())?.definition : undefined));
	}

	function setFill(index: number, entered?: string) {
		setFills((current) => {
			const next = [...current];
			next[index] = entered ?? "";
			return next;
		});
	}

	async function submit(overwrite = false) {
		if (!settingName) return setError(nameParts ? "Fill in each placeholder, without dots." : "Pick a setting.");
		setError(undefined);
		const setting = {
			applicationSettingId: editing?.applicationSettingId ?? 0,
			name: settingName,
			value,
			dataType: dataType ?? ApplicationSettingType.String,
			// Empty notes on a new row are left for the server to fill; on an edit they clear the row's notes
			notes: editing ? notes.trim() : notes.trim() || null,
			...segmentToSave(segment),
		};
		const result = await save.mutateAsync({ setting, overwrite }).catch(() => undefined);
		if (result?.status === AppSettingSaveStatus.Saved) return onClose();
		if (result?.status === AppSettingSaveStatus.Exists && result.existing) return setExisting(result.existing);
		setError(result?.error || SAVE_FAILED);
	}

	return (
		<>
			<MoveableDialog
				title={editing ? "Edit setting" : "Add setting"}
				open
				onOpenChange={(open) => !open && onClose()}
				fitContent
				movable
				resizable
				closeOnEscape
				closeOnClickOutside
				minSize={MIN_SIZE}
				footer={
					<div className={style.dialogButtons}>
						<Button types={[ButtonTypes.Outline]} onClick={onClose}>
							Cancel
						</Button>
						<Button types={[ButtonTypes.Primary]} onClick={() => submit()} disabled={save.isPending}>
							Save
						</Button>
					</div>
				}
			>
				<div className={style.dialogBody}>
					{fixedName ? (
						<p className={style.settingName}>{fixedName}</p>
					) : (
						<div className={style.formGrid}>
							<DefinitionCascade
								tree={tree}
								path={path}
								leaf={leaf}
								placeholder="Select"
								onChange={pickDefinition}
							/>
							{placeholders.map((placeholder, i) => (
								<PlaceholderInput
									key={`${i}-${placeholder}`}
									label={placeholder}
									options={placeholderChoices[i] ?? []}
									value={fills[i] ?? ""}
									onChange={(entered) => setFill(i, entered)}
								/>
							))}
						</div>
					)}
					{definition?.description && <p className={style.friendlyName}>{definition.description}</p>}

					<div className={style.formGrid}>
						<ValueInput
							type={dataType}
							enumOptions={definition?.enumOptions}
							value={value}
							onChange={setValue}
						/>
					</div>
					{editing?.isMasked && <p className={style.hint}>Leave blank to keep the current value.</p>}
					{savedNotAnOption && (
						<p className={style.hint}>
							<span className={style.unmatchedBadge}>Not an option</span> The saved value{" "}
							<q>{editing?.value}</q> matches none of this setting's options. Pick one to replace it.
						</p>
					)}

					<p className={style.hint}>
						Leave the segment at None for a company-wide row. With an office picked, the row is for that
						office only.
					</p>
					<div className={style.formGrid}>
						<SegmentSelectors segment={segment} offices={offices} onChange={setSegment} />
					</div>

					<TextArea
						label="Notes"
						value={notes}
						rows={2}
						changeHandler={(entered) => setNotes(entered ?? "")}
					/>

					{error && <p className={style.dialogError}>{error}</p>}
				</div>
			</MoveableDialog>
			{existing && (
				<OverwriteDialog
					name={settingName}
					existing={existing}
					value={value}
					enumOptions={enumOptions}
					onCancel={() => setExisting(undefined)}
					onOverwrite={() => {
						setExisting(undefined);
						void submit(true);
					}}
				/>
			)}
		</>
	);
}
